import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { EndConsumerSearch, EndConsumerSearchRow } from '@metron/shared';
import {
  parseEndConsumerSearchParams,
  SEARCH_DEFAULT_PAGE_SIZE,
  toEndConsumerSearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { EndConsumerSearchFilters } from '../components/end-consumer-search/EndConsumerSearchFilters.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useEndConsumerFilter } from '../hooks/useEndConsumerFilter.ts';
import { useCustomers } from '../hooks/useCustomers.ts';

const na = <span className='text-muted-foreground'>N/A</span>;

// Column ids match the spec's sortBy values, so useTableUrlState's `sortBy`
// param is directly the spec's.
const columns: ColumnDef<EndConsumerSearchRow>[] = [
  {
    accessorKey: 'email',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.email}</div>,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
    cell: ({ row }) => row.original.name || na,
  },
  {
    accessorKey: 'customerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => row.original.customerName ?? na,
  },
  {
    accessorKey: 'meterCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Meters' />,
    cell: ({ row }) => <div className='text-right'>{row.original.meterCount.toLocaleString()}</div>,
  },
  {
    accessorKey: 'lastMoveInDate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Last Move-in' />,
    cell: ({ row }) => row.original.lastMoveInDate ?? na,
  },
];

/**
 * End Consumer Search (Smart Search results, MET-3528)
 *
 * Searches end consumers across customers: the whole search lives in the URL,
 * filters render as editable chips, and paging/sorting use the shared
 * URL-backed table state. A row opens that customer's End Consumers page,
 * where account status and meters are managed.
 */
export function EndConsumerSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parseEndConsumerSearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = useEndConsumerFilter(spec);

  const setSpec = (next: EndConsumerSearch) => setSearchParams(toEndConsumerSearchParams(next));

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>End Consumer Search</h1>
        <p className='text-muted-foreground'>
          Find end consumers by customer, email or name, meter count, and move-in date.
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/end-consumers'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <EndConsumerSearchFilters
                spec={parsed.data}
                customers={customers}
                customersLoading={customersLoading}
                onChange={setSpec}
              />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} end consumer${
                    data?.total === 1 ? '' : 's'
                  }${isFetching ? ' · updating…' : ''}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.endConsumers ?? []}
                    onRowClick={(consumer) =>
                      navigate(`/customers/${consumer.customerId}?tab=consumers`)}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading
                      ? 'Searching…'
                      : 'No end consumers match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
